import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import staffService from "../../Services/staffService";
import StaffDetailsModal from "../../Components/StaffDetailsModal";

const Staff = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const getStaff = async () => {
      try {
        setLoading(true);
        const response = await staffService.fetchAll();
        setPatients(response.staffs);
      } catch (error) {
        toast.error("Error fetching staff");
      } finally {
        setLoading(false);
      }
    };
    getStaff();
  }, []);

  useEffect(() => {
    const filteredResult =
      patients?.filter((item) =>
        item?.full_name?.toLowerCase().includes(searchData.toLowerCase()),
      ) || [];
    setFilteredData(filteredResult);
  }, [searchData, patients]);

  const handleViewClick = (staff) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStaff(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const colWidths = ["70px", "160px", "140px", "160px", "110px", "80px"];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <>
      <div className="p-3 sm:p-4 font-sans">
        {/* ── Page Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
              Staff
            </p>
          </div>
          <Link to="AddEditStaff">
            <button className="flex items-center gap-1.5 px-8 text-[13px] py-3 text-xs font-medium text-white bg-[#1F2937] rounded-lg hover:bg-[#111827] transition focus:outline-none focus:ring-2 focus:ring-gray-300">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Staff
            </button>
          </Link>
        </div>

        {/* ── Search ── */}
        <div className="relative mb-4">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="search"
            placeholder="Search by name..."
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
            className="w-full sm:w-72 pl-9 pr-4 py-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* ══ MOBILE CARDS ══ */}
        <div className="block sm:hidden">
          {loading ? (
            <div className="text-center py-10 text-gray-400 text-xs">
              Loading...
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-xs">
              No staff found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredData.map((staff) => (
                <div
                  key={staff?.staff_id}
                  className="border border-gray-200 rounded-xl bg-white px-3 py-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-semibold text-gray-600 uppercase flex-shrink-0">
                        {staff?.full_name?.charAt(0) || "S"}
                      </div>
                      <div>
                        <p className="text-[12px] font-semibold text-gray-800">
                          {staff?.full_name}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          #{staff?.staff_id}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewClick(staff)}
                      className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition"
                    >
                      View
                    </button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {[
                      { label: "Designation", value: staff?.role_name },
                      { label: "Qualification", value: staff?.qualification },
                      {
                        label: "Appointed",
                        value: formatDate(staff?.hire_date),
                      },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between py-1.5">
                        <span className="text-[11px] text-gray-400 w-24">
                          {label}
                        </span>
                        <span className="text-[11px] text-gray-700 text-right flex-1 ml-2">
                          {value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ══ DESKTOP TABLE ══ */}
        <div className="hidden sm:block border rounded-xl overflow-hidden">
          <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
            <table
              className="w-full text-sm"
              style={{
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <ColGroup />
              <thead>
                <tr>
                  {[
                    { label: "Sr No.", align: "left" },
                    { label: "Full Name", align: "left" },
                    { label: "Designation", align: "left" },
                    { label: "Qualification", align: "left" },
                    { label: "Appointed", align: "center" },
                    { label: "Action", align: "center" },
                  ].map(({ label, align }, i) => (
                    <th
                      key={label}
                      className={`sticky top-0 z-20 bg-[#1F2937] text-${align} px-4 py-2.5 text-[11px] text-gray-100 uppercase font-medium ${
                        i < 5 ? "border-r border-white/10" : ""
                      }`}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-10 text-gray-400 text-[12px]"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-10 text-gray-400 text-[12px]"
                    >
                      No staff found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((staff, index) => (
                    <tr
                      key={staff?.staff_id}
                      className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                        index % 2 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-500 border-r border-gray-100">
                        {staff?.staff_id}
                      </td>
                      <td className="px-4 py-2.5 border-r border-gray-100">
                        <span className="text-[12px] font-medium text-gray-800 truncate">
                          {staff?.full_name}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                        {staff?.role_name || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                        {staff?.qualification || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 text-center border-r border-gray-100">
                        {formatDate(staff?.hire_date)}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => handleViewClick(staff)}
                          className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={6}
                    className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-medium text-gray-100 text-center border-t border-white/10"
                  >
                    {filteredData?.length || 0} total records
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <StaffDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        staffDetails={selectedStaff}
      />
    </>
  );
};

export default Staff;
